import React, { useState } from "react";
import { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useNavigate } from "../../../components/Navigate";
import MainLayout from "../../../components/layout/MainLayout";
import ScreenWrapper from "../../../components/layout/screenWrapper";
import { useHookstate } from "@hookstate/core";
import { DialogControl } from "../../../components/controls/DialogControl";
import _, { set } from "lodash";
import globalAuthStateStore from "../../../stores/globalAuthStateStore";
import { VirtualLibrarianService } from "../../../services/VirtualLibrarianService";
import GuardrailTable from "../components/virtualLibrarian/guardrails/table";
import GuardrailDetails from "../components/virtualLibrarian/guardrails/details";
import VirtualLibrarianMgmtMenu from "../components/virtualLibrarian/menu";
import ReactMarkdown from "react-markdown";
import VirtualLibrarianChatLog from "../components/virtualLibrarian/history/chatLogInterface";
import VirtualLibrarianAvailableContent from "../components/virtualLibrarian/content/main";
import "../virtualLibrarian.scss";
import VirtualLibrarianSettings from "../components/virtualLibrarian/settings/settings";
import ChatInputInterface from "../components/virtualLibrarian/chat/chatInputInterface";
import AdminPlayground from "../components/virtualLibrarian/playground/AdminPlayground";
import MasterPrompt from "../components/virtualLibrarian/masterPrompt/MasterPrompt";
import OrganizationAddEdit from "../components/virtualLibrarian/masterPrompt/OrganizationAddEdit";

const VirtualLibrarianMgmt = () => {
  const [selectedMenuItem, setSelectedMenuItem] = useState("masterPrompt");
  const [manageMode, setManageMode] = useState("list");
  const [loadingError, setLoadingError] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null);
  const [errors, setErrors] = useState(null);
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [guardrailToDelete, setGuardrailToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [systemPromptDialogOpen, setSystemPromptDialogOpen] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(undefined);

  const params = useParams();
  const [guardrailId, setGuardrailId] = new useState(
    params?.guardrailId === undefined ||
    params?.guardrailId?.toLowerCase() === "addnew"
      ? null
      : params?.guardrailId
  );
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const initialParams = new URLSearchParams(window.location.search);
    const viewParam = initialParams.get("view");
    setSelectedMenuItem(viewParam?.length > 0 ? viewParam : "masterPrompt");
  }, []);

  const authStore = useHookstate(globalAuthStateStore);

  const vlService = React.useMemo(
    () => new VirtualLibrarianService(authStore),
    [authStore]
  );
  const [guardrails, setGuardrails] = useState([]);

  const fetchGuardrails = async () => {
    const result = await vlService.getVirtualLibrarianGuardrails();
    if (result) {
      console.log("Guardrails fetched successfully:", result);
      setGuardrails(result);
    }
  };

  const fetchSystemPrompt = async () => {
    const result = await vlService.getVirtualLibrarianSystemPrompt();
    if (result) {
      console.log("System Prompt fetched successfully:", result);
      setSystemPrompt(result.Data.prompt);
    }
  };

  useEffect(() => {
    fetchGuardrails();
    fetchSystemPrompt();
  }, [vlService]);

  useEffect(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes("guardrails")) {
      setSelectedMenuItem("guardrails");
      if (params?.guardrailId?.toLowerCase() === "addnew") {
        setManageMode("add");
        setGuardrailId(undefined);
      } else if (params?.guardrailId) {
        setManageMode("edit");
        setGuardrailId(params?.guardrailId);
      } else {
        setManageMode("default");
        setGuardrailId(undefined);
      }
    }
  }, [params?.guardrailId]);

  const handleOnMenuItemSelected = (value, organization) => {
    const previousScreen = selectedMenuItem;
    setSelectedMenuItem(value);
    if (previousScreen !== value) {
      setManageMode("default");
    }

    navigate(`?view=${value}`, { replace: true });

    if (organization?.value?.length > 0) {
      setSelectedOrganization(organization);
    } else {
      setSelectedOrganization(undefined);
    }
  };

  const handleOnDelete = (item) => {
    setGuardrailToDelete(item);
  };

  const handleOnDeleteItem = async () => {
    const result = await vlService.deleteVirtualLibrarianGuardrail(
      guardrailToDelete.id
    );
    if (result.Success) {
      setGuardrails(guardrails.filter((x) => x.id !== guardrailToDelete.id));
      setGuardrailToDelete(null);
    } else {
      return {
        fail: true,
        messages: [
          `Failed to delete ${guardrailToDelete.PromptTitle}. `,
          result.Message.DisplayMessage,
        ],
      };
    }
  };

  const handleOnEdit = (id) => {
    navigate(`/admin/EchoAI/guardrails/${id}`);
  };

  const handleOnCancel = () => {
    navigate(`/admin/EchoAI/${selectedMenuItem}`);
    setManageMode("default");
    setLoadingError(null);
    setValidationErrors(null);
    setErrors(null);
  };

  const handleOnGuardrailCancel = () => {
    navigate("/admin/EchoAI");
    setManageMode("default");
    setLoadingError(null);
    setValidationErrors(null);
    setErrors(null);
  };

  const handleOnGuardrailSave = async (result) => {
    navigate("/admin/EchoAI");
    setManageMode("default");
    fetchGuardrails();
    fetchSystemPrompt();
    setLoadingError(null);
    setValidationErrors(null);
    setErrors(null);
  };

  return (
    <>
      <MainLayout
        errors={errors}
        messages={messages}
        validationErrors={validationErrors}
      >
        <DialogControl
          openDialog={guardrailToDelete}
          title={"Confirm Delete"}
          subTitle={`Are you sure you want to delete ${guardrailToDelete?.promptTitle}?`}
          onCancel={() => setGuardrailToDelete(null)}
          onOk={handleOnDeleteItem}
          okText={"Confirm"}
        >
          {deleteError && <div className="error">{deleteError}</div>}
        </DialogControl>
        <DialogControl
          openDialog={systemPromptDialogOpen}
          title={"System Prompt"}
          subTitle={`This is the system prompt used by the ECHO Enhanced Search.`}
          onCancel={() => setSystemPromptDialogOpen(false)}
          cancelText="Close"
          disableOk={true}
        >
          <div className="system-prompt-dialog">
            <div
              style={{
                marginTop: "5px",
                border: "1px solid #ccc",
                padding: "20px",
                borderRadius: "4px",
                minHeight: "100px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <ReactMarkdown>
                {systemPrompt || "*No content provided*"}
              </ReactMarkdown>
            </div>
          </div>
        </DialogControl>
        <ScreenWrapper
          loading={loading}
          loadingError={loadingError}
          onReturn={handleOnCancel}
          className="virtual-screen-wrapper"
        >
          <div className="virtual-librarian-mgmt">
            <div className="virtual-librarian-mgmt-chat-container">
              <VirtualLibrarianMgmtMenu
                onMenuItemSelected={handleOnMenuItemSelected}
                selectedMenuItem={selectedMenuItem}
                selectedOrganization={selectedOrganization}
              />
              {selectedMenuItem === "masterPrompt" && (
                <MasterPrompt guardrails={guardrails} />
              )}
              {selectedMenuItem.includes("organization") &&
                selectedOrganization && (
                  <MasterPrompt
                    guardrails={guardrails}
                    organization={selectedOrganization}
                  />
                )}
              {selectedMenuItem.includes("organization") &&
                selectedOrganization === undefined && (
                  <OrganizationAddEdit
                    guardrails={guardrails}
                    organization={selectedOrganization}
                  />
                )}
              {selectedMenuItem === "playground" && <AdminPlayground />}
              {selectedMenuItem === "guardrails" &&
                manageMode === "default" && (
                  <GuardrailTable
                    filteredItems={guardrails}
                    onDelete={handleOnDelete}
                    onEdit={handleOnEdit}
                    setSystemPromptDialogOpen={setSystemPromptDialogOpen}
                    navigate={navigate}
                  />
                )}
              {selectedMenuItem === "guardrails" && manageMode === "edit" && (
                <GuardrailDetails
                  guardrailId={guardrailId}
                  onSave={handleOnGuardrailSave}
                  onCancel={handleOnGuardrailCancel}
                  onValidationErrors={setValidationErrors}
                  onLoading={setLoading}
                  onErrors={setErrors}
                  onLoadingError={setLoadingError}
                />
              )}
              {selectedMenuItem === "guardrails" && manageMode === "add" && (
                <GuardrailDetails
                  guardrailId={guardrailId}
                  onSave={handleOnGuardrailSave}
                  onCancel={handleOnGuardrailCancel}
                  onValidationErrors={setValidationErrors}
                  onLoading={setLoading}
                  onErrors={setErrors}
                  onLoadingError={setLoadingError}
                />
              )}
              {selectedMenuItem === "content" && (
                <VirtualLibrarianAvailableContent />
              )}
              {selectedMenuItem === "history" && <VirtualLibrarianChatLog />}
              {selectedMenuItem === "settings" && <VirtualLibrarianSettings />}
            </div>
            {selectedMenuItem === "chat" && <ChatInputInterface />}
          </div>
        </ScreenWrapper>
      </MainLayout>
    </>
  );
};

export default VirtualLibrarianMgmt;
